<template>
    <div id="cohorts-tab" data-testid="cohorts-tab">
        <CohortsForum
            v-if="forumCohortId"
            :cohort-id="forumCohortId"
            :cohort-name="forumCohortName"
            @close="closeForum"
        />

        <template v-else>
        <div class="d-flex justify-content-between align-items-center mb-3">
            <div class="page-separator mb-0 flex-grow-1">
                <div class="page-separator__text">Turmas</div>
            </div>
            <button
                type="button"
                class="btn btn-success btn-sm ml-3"
                data-testid="cohort-create-open"
                @click="openCreate"
            >
                Nova turma
            </button>
        </div>

        <div class="table-responsive">
            <table class="table mb-0 table-nowrap thead-border-top-0" data-testid="cohorts-table">
                <thead>
                <tr>
                    <th>Nome</th>
                    <th class="text-center">Status</th>
                    <th class="text-center">Vagas</th>
                    <th class="text-center">Inscritos</th>
                    <th class="text-center">Início</th>
                    <th class="text-center">Fim</th>
                    <th class="text-center">Ações</th>
                </tr>
                </thead>
                <tbody>
                <tr v-if="!cohorts.length && !loading">
                    <td colspan="7" class="text-center text-muted">
                        Nenhuma turma cadastrada.
                    </td>
                </tr>
                <tr v-for="cohort in cohorts" :key="cohort.id" data-testid="cohort-row">
                    <td>{{ cohort.name }}</td>
                    <td class="text-center">
                        <span
                            class="badge"
                            :class="cohort.status === 'open' ? 'badge-success' : 'badge-secondary'"
                            data-testid="cohort-status"
                        >
                            {{ cohort.status === 'open' ? 'Aberta' : 'Fechada' }}
                        </span>
                    </td>
                    <td class="text-center" data-testid="cohort-vacancies">
                        {{ cohort.vacancies_taken || 0 }}/{{ cohort.vacancies }}
                        <small class="d-block text-muted">
                            {{ cohort.vacancies_left != null ? cohort.vacancies_left : '—' }} restantes
                        </small>
                    </td>
                    <td class="text-center" data-testid="cohort-mentees-count">
                        {{ cohort.mentees_count != null ? cohort.mentees_count : '—' }}
                    </td>
                    <td class="text-center">{{ formatDate(cohort.starts_at) }}</td>
                    <td class="text-center">{{ formatDate(cohort.ends_at) }}</td>
                    <td class="text-center">
                        <button
                            type="button"
                            class="btn btn-sm btn-outline-info mx-1"
                            title="Editar"
                            data-testid="cohort-edit"
                            @click="openEdit(cohort)"
                        >
                            <i class="fas fa-pencil-alt"></i>
                        </button>
                        <button
                            type="button"
                            class="btn btn-sm btn-outline-warning mx-1"
                            :title="cohort.status === 'open' ? 'Fechar turma' : 'Abrir turma'"
                            data-testid="cohort-toggle-status"
                            :disabled="saving"
                            @click="toggleStatus(cohort)"
                        >
                            <i :class="cohort.status === 'open' ? 'fas fa-lock' : 'fas fa-lock-open'"></i>
                        </button>
                        <button
                            type="button"
                            class="btn btn-sm btn-outline-primary mx-1"
                            title="Ver inscritos"
                            data-testid="cohort-enrollments"
                            @click="openEnrollments(cohort)"
                        >
                            <i class="fas fa-users"></i>
                        </button>
                        <button
                            type="button"
                            class="btn btn-sm btn-outline-success mx-1"
                            title="Fórum da turma"
                            data-testid="cohort-forum"
                            @click="openForum(cohort)"
                        >
                            <i class="fas fa-comments"></i>
                        </button>
                    </td>
                </tr>
                </tbody>
            </table>
        </div>
        </template>

        <b-modal
            id="modal-cohort-form"
            v-model="formModal"
            :title="editingId ? 'Editar turma' : 'Nova turma'"
            ok-title="Salvar"
            cancel-title="Cancelar"
            data-testid="cohort-form-modal"
            @ok="onFormOk"
        >
            <div class="form-group">
                <label for="cohort-name">Nome</label>
                <input
                    id="cohort-name"
                    v-model="form.name"
                    type="text"
                    class="form-control"
                    required
                    data-testid="cohort-name-input"
                >
            </div>
            <div class="form-group">
                <label for="cohort-vacancies">Vagas</label>
                <input
                    id="cohort-vacancies"
                    v-model.number="form.vacancies"
                    type="number"
                    min="1"
                    class="form-control"
                    required
                    data-testid="cohort-vacancies-input"
                >
            </div>
            <div class="form-group">
                <label for="cohort-starts-at">Início (opcional)</label>
                <input
                    id="cohort-starts-at"
                    v-model="form.starts_at"
                    type="datetime-local"
                    class="form-control"
                    data-testid="cohort-starts-at-input"
                >
            </div>
            <div class="form-group mb-0">
                <label for="cohort-ends-at">Fim (opcional)</label>
                <input
                    id="cohort-ends-at"
                    v-model="form.ends_at"
                    type="datetime-local"
                    class="form-control"
                    data-testid="cohort-ends-at-input"
                >
            </div>
        </b-modal>

        <b-modal
            id="modal-cohort-enrollments"
            v-model="enrollmentsModal"
            :title="'Inscritos — ' + (selectedCohortName || '')"
            ok-only
            ok-title="Fechar"
            size="lg"
            data-testid="cohort-enrollments-modal"
        >
            <div v-if="enrollmentsLoading" class="text-center text-muted py-3">Carregando…</div>
            <div v-else-if="!enrollments.length" class="text-center text-muted py-3">
                Nenhum inscrito nesta turma.
            </div>
            <div v-else class="table-responsive">
                <table class="table table-sm mb-0" data-testid="cohort-enrollments-table">
                    <thead>
                    <tr>
                        <th>Nome</th>
                        <th>E-mail</th>
                        <th class="text-center">Status</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="item in enrollments" :key="item.id">
                        <td>{{ item.name || '—' }}</td>
                        <td>{{ item.email || '—' }}</td>
                        <td class="text-center">{{ item.status || '—' }}</td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </b-modal>
    </div>
</template>

<script>
import http from "@/http";
import {endLoading, startLoading} from "@/components/composables/spinners";
import CohortsForum from "@/views/mentoring/details/cohorts-forum.vue";

export default {
    name: 'MentoringCohorts',

    components: {
        CohortsForum,
    },

    props: {
        mentoringId: {
            type: [Number, String],
            required: true,
        },
    },

    data() {
        return {
            cohorts: [],
            loading: false,
            saving: false,
            formModal: false,
            editingId: null,
            form: {
                name: '',
                vacancies: 1,
                starts_at: '',
                ends_at: '',
                status: 'open',
            },
            enrollmentsModal: false,
            enrollmentsLoading: false,
            enrollments: [],
            selectedCohortName: '',
            forumCohortId: null,
            forumCohortName: '',
        };
    },

    watch: {
        mentoringId: {
            immediate: true,
            handler(id) {
                if (id) this.fetchCohorts();
            },
        },
    },

    methods: {
        formatDate(value) {
            if (!value) return '—';
            const date = new Date(String(value).replace(' ', 'T'));
            if (Number.isNaN(date.getTime())) return value;
            return date.toLocaleString('pt-BR');
        },

        toApiDateTime(localValue) {
            if (!localValue) return null;
            return localValue.length === 16 ? `${localValue}:00` : localValue;
        },

        toDatetimeLocal(value) {
            if (!value) return '';
            const date = new Date(String(value).replace(' ', 'T'));
            if (Number.isNaN(date.getTime())) return '';
            const pad = n => String(n).padStart(2, '0');
            return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
        },

        resetForm() {
            this.form = {
                name: '',
                vacancies: 1,
                starts_at: '',
                ends_at: '',
                status: 'open',
            };
            this.editingId = null;
        },

        openCreate() {
            this.resetForm();
            this.formModal = true;
        },

        openEdit(cohort) {
            this.editingId = cohort.id;
            this.form = {
                name: cohort.name || '',
                vacancies: cohort.vacancies || 1,
                starts_at: this.toDatetimeLocal(cohort.starts_at),
                ends_at: this.toDatetimeLocal(cohort.ends_at),
                status: cohort.status || 'open',
            };
            this.formModal = true;
        },

        onFormOk(bvModalEvt) {
            bvModalEvt.preventDefault();
            this.saveForm();
        },

        fetchCohorts() {
            if (!this.mentoringId) return;
            this.loading = true;
            startLoading('cohorts-tab');
            http.get(`mentorings/${this.mentoringId}/cohorts`)
                .then(response => {
                    this.cohorts = Array.isArray(response.data) ? response.data : [];
                })
                .catch(e => {
                    console.error(e);
                    this.$notifyError(e.response?.data || e);
                    this.cohorts = [];
                })
                .finally(() => {
                    this.loading = false;
                    endLoading('cohorts-tab');
                });
        },

        saveForm() {
            if (!this.form.name || !this.form.vacancies || this.form.vacancies < 1) {
                this.$notifyError('Informe nome e vagas (mínimo 1).');
                return;
            }

            const payload = {
                name: this.form.name,
                vacancies: Number(this.form.vacancies),
                status: this.form.status || 'open',
                starts_at: this.toApiDateTime(this.form.starts_at),
                ends_at: this.toApiDateTime(this.form.ends_at),
            };

            this.saving = true;
            const request = this.editingId
                ? http.put(`cohorts/${this.editingId}`, payload)
                : http.post(`mentorings/${this.mentoringId}/cohorts`, payload);

            request
                .then(() => {
                    this.$notifySuccess(this.editingId ? 'Turma atualizada!' : 'Turma criada!');
                    this.formModal = false;
                    this.resetForm();
                    this.fetchCohorts();
                })
                .catch(e => {
                    console.error(e);
                    this.$notifyError(e.response?.data || e);
                })
                .finally(() => {
                    this.saving = false;
                });
        },

        toggleStatus(cohort) {
            const next = cohort.status === 'open' ? 'closed' : 'open';
            this.saving = true;
            http.put(`cohorts/${cohort.id}`, {status: next})
                .then(() => {
                    this.$notifySuccess(next === 'open' ? 'Turma aberta!' : 'Turma fechada!');
                    this.fetchCohorts();
                })
                .catch(e => {
                    console.error(e);
                    this.$notifyError(e.response?.data || e);
                })
                .finally(() => {
                    this.saving = false;
                });
        },

        openEnrollments(cohort) {
            this.selectedCohortName = cohort.name || '';
            this.enrollments = [];
            this.enrollmentsModal = true;
            this.enrollmentsLoading = true;
            http.get(`cohorts/${cohort.id}`)
                .then(response => {
                    const data = response.data || {};
                    this.enrollments = Array.isArray(data.enrollments)
                        ? data.enrollments
                        : (Array.isArray(data.mentees) ? data.mentees : []);
                })
                .catch(e => {
                    console.error(e);
                    this.$notifyError(e.response?.data || e);
                    this.enrollments = [];
                })
                .finally(() => {
                    this.enrollmentsLoading = false;
                });
        },

        openForum(cohort) {
            this.forumCohortId = cohort.id;
            this.forumCohortName = cohort.name || '';
        },

        closeForum() {
            this.forumCohortId = null;
            this.forumCohortName = '';
        },
    },
};
</script>
